import React from "react";
import ViewDocument from "../../ui/ViewDocument";

const CareBangladesh = () => {
  return (
    <div
      id="care-economy-bangladesh"
      className="space-y-4 text-softBlack leading-loose text-base font-normal !mt-14 scroll-mt-24"
    >
      <h2 className="text-xl font-bold !mt-10 mb-4">
        The Care Economy in Bangladesh
      </h2>

      <h3 className="text-base font-bold mt-4 mb-3">
        Current status of the care economy
      </h3>
      <p>
        The ILO and UN Women have been working with the Ministry of Women and
        Child Affairs, Bangladesh, towards improving access to gender-sensitive
        care policies and services, enhancing social and physical
        infrastructure, creating decent work opportunities, and addressing
        social norms and the gendered division of labour. In May 2024, the ILO
        and UN Women presented the idea of{" "}
        <a
          href="https://www.ilo.org/resource/news/government-and-development-partners-unite-advance-care-economy-bangladesh"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          a joint program
        </a>
        , which would integrate gender-transformative measures into labor market
        policies and enhance access to care services.
      </p>

      <h3 className="text-base font-semibold italic">
        Low female labor force participation
      </h3>
      <p>
        According to World Bank data, female labor force participation in
        Bangladesh increased from 24 percent in 2000 to 36 percent in 2017, due
        to the growing engagement of women in the ready-made garment industry,
        which employs over 3 million women out of a total of 4.4 million
        employees in the country.{" "}
      </p>
      <p>
        <a
          href="https://genderdata.worldbank.org/en/economies/bangladesh"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          ILO estimates
        </a>{" "}
        state that in 2024, women’s labor force participation stood at 44.2
        percent. However, according to a study by the Bangladesh Bureau of
        Statistics, cited in a World Bank{" "}
        <a
          href="https://documents1.worldbank.org/curated/en/615251627311387137/pdf/Women-s-Economic-Participation-Time-Use-and-Access-to-Childcare-in-Urban-Bangladesh.pdf#:~:text=1%20Yet%2C%20at%2036%20percent%20in%202017%2C,economic%20growth%20and%20increases%20in%20women's%20education."
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          paper
        </a>{" "}
        female labor force participation in urban areas declined from 34.5
        percent to 30.8 percent between 2010 and 2017, partly due to the lack of
        childcare facilities. During the same period, rural female labor force
        participation increased to 38.5% from 36.4%. The world average of female
        labour force participation in 2020 stood at 47%.
      </p>

      <p>
        The 2018 Dhaka Low Income Area Gender, Inclusion, and Poverty (DIGNITY){" "}
        <a
          href="https://www.ifc.org/en/insights-reports/2010/tackling-childcare-the-business-benefits-and-challenges-of-employer-supported-childcare-in-bangladesh"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          survey
        </a>{" "}
        conducted by the World Bank found that women in low-income areas of
        Dhaka spent 5.16 hours a day on childcare and domestic work, in
        comparison to men, who only spent 0.34 hours. An{" "}
        <a
          href="https://data.unwomen.org/sites/default/files/documents/Publications/2024/Bangladesh_TUS-Summary-Brief_0524.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          analysis
        </a>{" "}
        of Bangladesh Time Use Survey Data from 2021 found that women spent 7.3
        times more time on unpaid domestic and care work than men, spending 5.9
        hours per day compared to men’s contribution of 0.8 hours. According to{" "}
        <a
          href="https://unece.org/sites/default/files/2024-04/1C_3_Measuring%20the%20Value%20of%20Unpaid%20Household%20Work%20in%20Bangladesh_ADB.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          calculations
        </a>{" "}
        by the Asian Development Bank, women's unpaid domestic and care work
        contributions accounted for approximately 18 percent of Bangladesh’ GDP
        in 2021. According{" "}
        <a
          href="https://www.thedailystar.net/news/bangladesh/rights/news/womens-unpaid-labour-worth-tk-570000cr-3981991"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          to calculations
        </a>{" "}
        by the Bangladesh Bureau of Statistics, unpaid domestic and care work in
        2021 overall was valued at Tk 670,000 crore (about 54.86 billion USD)
        which is equivalent to 18.9 percent of the country's GDP — with women
        performing 85 percent (16.14 percent of GDP) of this labor.
      </p>

      <h3 className="text-base italic !mt-5">
        Barriers to women’s labor force participation
      </h3>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          According to a{" "}
          <a
            href="https://documents1.worldbank.org/curated/en/265491570091030693/pdf/What-Works-for-Working-Women-Understanding-Female-Labor-Force-Participation-in-Urban-Bangladesh.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            2019 World Bank survey
          </a>{" "}
          on female labor force participation in urban Bangladesh, women with
          children under the age of five are 15 percentage points less likely to
          be engaged in work than women with no children, or with older
          children. This is more prevalent in urban areas, and limited access to
          childcare was the main reason
        </li>
        <li>
          The same survey found that women have limited access to public
          transportation, and do not feel safe in public spaces.
        </li>
        <li>
          A 2018{" "}
          <a
            href="https://www.unicef.org/bangladesh/sites/unicef.org.bangladesh/files/2018-07/BB4C-Baseline%20Assessment-Executive%20Summary_Design_FINAL_without%20cutmark_v03.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            survey of garment workers
          </a>{" "}
          by the United Nations Children's Fund (UNICEF) in 2018 in the
          ready-made clothing industry found that almost two-thirds of workers
          had migrated to urban settings, leaving their children at home with
          family in rural areas. A large proportion of workers expressed a
          preference for having their children cared for by grandparents, with
          97 percent of surveyed workers reporting that they did not use daycare
          facilities available at their workplaces.
        </li>
        <li>
          An{" "}
          <a
            href="https://www.ethicaltrade.org/sites/default/files/shared_resources/ETI_GIZ%20Study%20on%20Women%20Workers.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Ethical Trading Initiative survey
          </a>{" "}
          in 2023 found that the main factors for the fall in labor force
          participation of women in the garment industry were childcare
          responsibilities, pregnancy, and difficulty balancing home and work
          responsibilities.
        </li>
        <li>
          Although the law mandates that employers with more than 40 female
          employees provide childcare to children until the age of six, only 23
          percent of companies do so, according to{" "}
          <a
            href="https://www.ifc.org/content/dam/ifc/doc/mgrt/ifc-tackling-childcare-in-bangladesh-final-report.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            research
          </a>{" "}
          conducted in 2019 by the International Finance Corporation.
        </li>
      </ul>

      <h3 className="text-base font-bold !mt-5">Care economy initiatives</h3>
      <p>
        The ILO is institutionalizing caregiving for children and the elderly
        through the development of training packages for young people to become
        employed as caregivers, either within Bangladesh or overseas. Bangladesh
        also appears to be prioritizing childcare with the launch of a{" "}
        <a
          href="https://www.ilo.org/resource/news/government-bangladesh-launch-roadmap-childcare-bangladesh#:~:text=A%20presentation%20on%20the%20supporting,working%20conditions%20for%20childcare%20workers."
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          roadmap
        </a>{" "}
        on the care economy in November 2024, which sets out short-term,
        medium-term and long-term milestones to be achieved over the next ten
        years.
      </p>
      <p className="italic">These include:</p>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          Strengthening governance and promoting fair wages, safety and training
          to create a cadre of skilled childcare workers (within two years)
        </li>
        <li>
          Improving the quality of Early Childhood Care and Education (ECCE)
          services by expanding coverage and providing decent working conditions
          and necessary certifications for workers (within five years)
        </li>
        <li>
          Implementing a complete nationwide plan to provide accessible and
          affordable care which meets international standards, and establishing
          childcare as a recognized profession with social protections and a
          safe work environment (within 10 years)
        </li>
      </ul>
      <p>
        Phulki, an NGO, is helping set up affordable daycare facilities in urban
        Bangladesh and in garment factories. The organization says that it has
        opened 97 factory and community-based daycare centers, and supported
        1469 domestic workers through their training programmes,{" "}
        <a
          href="https://www.the-care-economy-knowledge-hub.org/wp-content/uploads/2023/04/40-Asia-Phulki-V2.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          as of 2021
        </a>
        .
      </p>

      <h3 className="text-base font-bold !mt-5">Expert recommendations</h3>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          <a
            href="https://www.ilo.org/resource/news/government-and-development-partners-unite-advance-care-economy-bangladesh"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            According to the ILO
          </a>
          , allocating 3.99 percent of the GDP to care sectors could generate
          about seven million new jobs by 2035, with 91 percent of these being
          formal jobs for women.
        </li>
        <li>
          The Ethical Trading Initiative presents recommendations to the
          government to invest in public childcare facilities, align maternity
          protections with international guidelines and bring in stakeholders
          including buyers and business representatives for collective decisions
          on garment workers’ issues. It also provides recommendations to
          businesses, workers’ representatives, and civil society organizations.
        </li>
        <li>
          <a
            href="https://documents1.worldbank.org/curated/en/265491570091030693/pdf/What-Works-for-Working-Women-Understanding-Female-Labor-Force-Participation-in-Urban-Bangladesh.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            The World Bank recommends
          </a>{" "}
          that making public spaces and travel more accessible and friendly for
          women, and changing social norms to make female work more socially
          acceptable could help increase female labor force participation.
        </li>
      </ul>

      <ViewDocument
        description="This document contains a list of schemes, policies and practices in Bangladesh, which directly and indirectly benefit caregivers and their dependents"
        documentUrl="https://sapient-dev-bucket-projects.s3.ap-south-1.amazonaws.com/noora-health/uploads/75981869215756324505.Care-Economy---Bangladesh---Schemes-and-policies-supporting-caregivers.pdf"
        className="!my-8"
      />
    </div>
  );
};

export default CareBangladesh;
